import {
  ConfirmationResult,
  GoogleAuthProvider,
  RecaptchaVerifier,
  signInAnonymously,
  signInWithPhoneNumber,
  signInWithPopup,
  signOut,
  updateProfile,
} from '@firebase/auth';
import { TaskAlt } from '@mui/icons-material';
import { Box, Divider, Link, Tab, Tabs, Typography } from '@mui/material';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input';
import { useEffect, useRef, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import addUserToSendPulse from '../api/user/addUserToSendPulse';
import { User } from '../apiTypes';
import Form from '../components/forms/Form';
import FormTextField from '../components/forms/FormTextField';
import GoogleIcon from '../components/GoogleIcon';
import LangSelect from '../components/LangSelect';
import Logo from '../components/Logo';
import PrimaryButton from '../components/PrimaryButton';
import { t } from '../constants/translations';
import { useLocale } from '../contexts/locale.context';
import { auth, db } from '../firebase';
import { sendLogin, sendSignUp } from '../utils/analytics';
import { setDoc } from '../utils/api';
import { createConfirmRes, sendSmsBySmsClub, smsProvider } from '../utils/sms';

type TabName = 'login' | 'register';

declare global {
  interface Window {
    recaptchaVerifier: RecaptchaVerifier;
    confirmationResult: ConfirmationResult;
  }
}

type FormType = {
  name: string;
  email: string;
  phone: string;
  code: string;
};

const AuthPage = () => {
  const [userAuth] = useAuthState(auth);
  const [params] = useSearchParams();
  const initTab = params.get('tab');
  const { lang } = useLocale();
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [tab, setTab] = useState<TabName>(initTab === 'register' || initTab === 'login' ? initTab : 'register');
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const confirmResRef = useRef('');

  const isRegister = tab === 'register';

  const form = useForm<FormType>({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      code: '',
    },
  });
  const email = form.watch('email');
  const name = form.watch('name');
  const phone = form.watch('phone');
  const code = form.watch('code');

  const loginWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    const res = await signInWithPopup(auth, provider);
    if (res.user.email) {
      const q = query(collection(db, 'users'), where('email', '==', res.user.email));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        const userDoc: User = {
          displayName: res.user.displayName || '',
          photoURL: res.user.photoURL || '',
          email: res.user.email,
          phone: res.user.phoneNumber || '',
        };

        sendSignUp('Google', userDoc.email, userDoc.phone);
        await addUserToSendPulse(userDoc, lang);
        await setDoc<User>(`users/${res.user.email}`, userDoc);
      } else {
        sendLogin('Google', res.user.email);
      }
    }
  };

  const sendSms = async () => {
    setIsAuthenticating(true);
    if (phone === '+380 67 333 3333') {
      auth.settings.appVerificationDisabledForTesting = true;
    }
    window.recaptchaVerifier = new RecaptchaVerifier(auth, 'sign-in-button', { size: 'invisible', callback: () => {} });
    await window.recaptchaVerifier.render();
    await window.recaptchaVerifier.verify();

    try {
      if (smsProvider === 'google') {
        const confirmationResult = await signInWithPhoneNumber(auth, phone, window.recaptchaVerifier);
        window.confirmationResult = confirmationResult;
      }

      if (smsProvider === 'smsClub') {
        confirmResRef.current = createConfirmRes();

        const res = await sendSmsBySmsClub({
          phone: phone.replace(/[\s+]+/g, ''),
          code: confirmResRef.current,
          lang,
        });

        if (res?.data?.status !== 200) {
          throw new Error('Failed to send SMS');
        }
      }

      setShowCodeInput(true);
    } catch (e) {
      form.setError('phone', { message: t.something_went_wrong[lang] });
    } finally {
      setIsAuthenticating(false);
    }
  };

  const loginWithPhone = async () => {
    setIsAuthenticating(true);
    try {
      if (smsProvider === 'google') {
        const { user } = await window.confirmationResult.confirm(code);

        const q = query(collection(db, 'users'), where('phone', '==', user.phoneNumber));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty && email && name) {
          if (isRegister) {
            const userDoc: User = {
              displayName: name,
              photoURL: '',
              email,
              phone: user.phoneNumber || '',
            };

            sendSignUp('SMS', userDoc.email, userDoc.phone);
            await addUserToSendPulse(userDoc, lang);
            await setDoc<User>(`users/${user.phoneNumber}`, userDoc);
          } else {
            setTab('register');
          }
        } else {
          sendLogin('SMS', email, user.phoneNumber || '');
        }
      }
      if (smsProvider === 'smsClub' && code === confirmResRef.current) {
        const { user } = await signInAnonymously(auth);
        const clearPhone = phone.replace(/\s+/g, '');
        await updateProfile(user, { displayName: clearPhone });

        const q = query(collection(db, 'users'), where('phone', '==', clearPhone));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty && email && name) {
          if (isRegister) {
            const userDoc: User = {
              displayName: name,
              photoURL: '',
              email,
              phone: clearPhone,
            };

            sendSignUp('SMS', userDoc.email, userDoc.phone);
            await addUserToSendPulse(userDoc, lang);
            await setDoc<User>(`users/${clearPhone}`, userDoc);
          } else {
            setTab('register');
          }
        } else {
          sendLogin('SMS', email, user.phoneNumber || '');
        }
      }
    } catch (e) {
      form.setError('code', { message: t.something_went_wrong[lang] });
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleSubmit = () => {
    if (!matchIsValidTel(phone)) {
      return;
    }

    if (!showCodeInput) {
      sendSms();
    }
  };

  useEffect(() => {
    if (userAuth?.email || userAuth?.phoneNumber) {
      form.setValue('name', userAuth.displayName || '');
      form.setValue('email', userAuth.email || '');
      form.setValue('phone', userAuth.phoneNumber || '');
    }
  }, [form, userAuth?.displayName, userAuth?.email, userAuth?.phoneNumber]);

  return (
    <Box px={4} display="flex" flexDirection="column" alignItems="center">
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-end',
          p: 2,
        }}
      >
        <LangSelect />
      </Box>

      <Form form={form} onSubmit={handleSubmit}>
        <Box sx={{ mx: 'auto', mb: 2, textAlign: 'center', maxWidth: 400, width: '100%' }}>
          <Logo sx={{ mb: 3, width: 200 }} />

          <Tabs
            centered
            variant="fullWidth"
            value={tab}
            onChange={(e, v) => {
              signOut(auth);
              setTab(v);
            }}
            sx={{ mb: 3 }}
          >
            <Tab label={t.registration[lang]} value="register" />
            <Tab label={t.login[lang]} value="login" disabled={isRegister && showCodeInput} />
          </Tabs>

          {(userAuth?.email || userAuth?.phoneNumber) && !showCodeInput && (
            <Typography color="GrayText" sx={{ mb: 2 }}>
              {t.no_account_found[lang]}
            </Typography>
          )}

          {showCodeInput && (
            <Typography color="GrayText" sx={{ mb: 2 }}>
              {t.sms_code_sent[lang]}
            </Typography>
          )}

          {showCodeInput && (
            <FormTextField
              name="code"
              label={t.code[lang]}
              required
              rules={{ minLength: 6, maxLength: 6, required: true }}
              fullWidth
              sx={{ mb: 2 }}
            />
          )}

          {!showCodeInput && (
            <>
              <Box mb={2} textAlign="center">
                <Divider>
                  <Typography color="GrayText">
                    {isRegister ? t.register_with_phone[lang] : t.login_with_phone[lang]}
                  </Typography>
                </Divider>
              </Box>

              {isRegister && (
                <FormTextField
                  name="name"
                  label={t.userName[lang]}
                  required
                  rules={{ required: true }}
                  fullWidth
                  sx={{ mb: 2 }}
                  disabled={isAuthenticating}
                />
              )}

              {isRegister && (
                <FormTextField
                  name="email"
                  label={t.email[lang]}
                  required
                  rules={{ pattern: /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/g, required: true }}
                  fullWidth
                  sx={{ mb: 2 }}
                  disabled={isAuthenticating}
                />
              )}

              <MuiTelInput
                fullWidth
                label={t.phone[lang]}
                defaultCountry={lang === 'uk' || lang === 'ru' ? 'UA' : 'ES'}
                color="secondary"
                required
                value={phone}
                name="phone"
                error={
                  (form.formState.isSubmitted && !matchIsValidTel(phone)) || !!form.formState.errors.phone?.message
                }
                helperText={form.formState.errors.phone?.message}
                onChange={(v) => form.setValue('phone', v)}
                disabled={isAuthenticating}
                sx={{
                  bgcolor: (t) => t.palette.background.paper,
                  boxShadow: 'none',
                  borderRadius: 1,
                  borderWidth: 2,
                  mb: 2,
                }}
              />
            </>
          )}

          {showCodeInput && (
            <PrimaryButton
              color="secondary"
              fullWidth
              variant="contained"
              sx={{ mb: 4, height: 52 }}
              onClick={loginWithPhone}
              loading={isAuthenticating}
            >
              {t.confirm[lang]}
            </PrimaryButton>
          )}

          {!showCodeInput && (
            <PrimaryButton
              color="secondary"
              fullWidth
              variant="contained"
              sx={{ mb: 4, height: 52 }}
              type="submit"
              id="sign-in-button"
              loading={isAuthenticating}
              disabled={!!form.formState.errors.phone?.message}
            >
              {isRegister ? t.register[lang] : t.login[lang]}
            </PrimaryButton>
          )}

          <>
            <Box mb={2} textAlign="center">
              <Divider>
                <Typography component="span" color="GrayText">
                  {t.or[lang]}
                </Typography>
              </Divider>
            </Box>

            <PrimaryButton
              color="secondary"
              fullWidth
              variant="outlined"
              sx={{ mb: 4, height: 52 }}
              onClick={loginWithGoogle}
              startIcon={<GoogleIcon />}
            >
              {isRegister ? t.register_with_google[lang] : t.login_with_google[lang]}
            </PrimaryButton>
          </>

          {isRegister && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 4, color: (t) => t.palette.text.secondary }}>
              <TaskAlt fontSize="small" color="success" />
              <Typography variant="body2" textAlign="left">
                {t.agree_terms[lang]}{' '}
                <Link href={`https://firemenu.net/${lang}/terms`} target="_blank" variant="body2" color="secondary">
                  {t.terms_and_conditions[lang]}
                </Link>
                {', '}
                <Link href={`https://firemenu.net/${lang}/privacy`} target="_blank" variant="body2" color="secondary">
                  {t.privacy_policy[lang]}
                </Link>{' '}
                {t.and[lang]}{' '}
                <Link href={`https://firemenu.net/${lang}/cookies`} target="_blank" variant="body2" color="secondary">
                  {t.cookie_policy[lang]}
                </Link>
              </Typography>
            </Box>
          )}

          {/* <AuthHelper /> */}
        </Box>
      </Form>
    </Box>
  );
};

export default AuthPage;
